import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import { useUserStore } from '@Auth/store';
import { authProvider } from '@helpers/auth';
import type { ResolvedIdp } from '@helpers/auth/domainIdpMap';
import { useRemoveGlobalLoader } from '@hooks/useRemoveGlobalLoader';
import { isEmpty } from 'lodash-es';

import Nav from '../../../routes';
import AuthLayout from '../AuthLayout';

import EmailStep from './EmailStep';
import { NO_CONTRACT_BUILDER_ACCESS_MESSAGE, SSO_SIGN_IN_FAILED_MESSAGE } from './errorMessages';
import PasswordStep from './PasswordStep';
import { consumeSsoAttempt } from './ssoAttempt';
import SsoStep from './SsoStep';

type Step =
  | { name: 'email'; email?: string }
  | { name: 'password'; email: string; fromSsoProvider?: string }
  | { name: 'sso'; email: string; provider: string; error?: string };

// Cognito PreSignUp (backend/events/cognito-handlers/pre-sign-up.ts) throws exactly this message
// when the federated identity has no provisioned Contract Builder account.
const resolveSsoErrorMessage = (errorDescription: string | null): string =>
  errorDescription?.toLowerCase().includes('user not found')
    ? NO_CONTRACT_BUILDER_ACCESS_MESSAGE
    : SSO_SIGN_IN_FAILED_MESSAGE;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [step, setStep] = useState<Step>({ name: 'email', email: location.state?.email });
  const { authErrorCode, isLoggedIn, setUser } = useUserStore(({ authErrorCode, isLoggedIn, setUser }) => ({
    authErrorCode,
    isLoggedIn,
    setUser,
  }));
  const removeGlobalLoader = useRemoveGlobalLoader();

  useEffectOnce(() => {
    // Cognito redirects failed SAML/SSO attempts back here with ?error=...&error_description=...
    // instead of anywhere step-scoped, so recover the in-flight attempt to show the error on Step 2.
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('error');

    if (ssoError) {
      // Preserve the existing history state (react-router's {usr, key, idx}) — only the URL's
      // query string needs stripping here.
      window.history.replaceState(window.history.state, '', window.location.pathname);
      const attempt = consumeSsoAttempt();
      const errorMessage = resolveSsoErrorMessage(params.get('error_description'));

      setStep(
        attempt
          ? { name: 'sso', email: attempt.email, provider: attempt.provider, error: errorMessage }
          : { name: 'email' },
      );
      setIsLoading(false);
      return;
    }

    (async () => {
      // if we're already in an error state (from interceptor), then we want to go directly to login => error
      if (authErrorCode && !isLoggedIn) {
        setIsLoading(false);
        return;
      }

      try {
        const currentUserInfo = await authProvider.attemptGetUserInfo();
        const hasUserInfo = !isEmpty(currentUserInfo);

        setIsLoading(false);
        setUser(hasUserInfo ? currentUserInfo : null);
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    })();
  });

  useEffect(() => {
    if (!isLoading) {
      removeGlobalLoader();
    }
  }, [isLoading, removeGlobalLoader]);

  useEffect(() => {
    if (isLoggedIn && !isLoading) {
      const pathName = location.state?.from?.pathname?.trim() ?? '';
      navigate(pathName || Nav.Submissions);
    }
  }, [isLoggedIn, location, navigate, isLoading]);

  const handleResolved = (email: string, result: ResolvedIdp) => {
    if (result.type === 'sso') {
      setStep({ name: 'sso', email, provider: result.provider });
    } else {
      setStep({ name: 'password', email });
    }
  };

  const handleBack = () => {
    // Password step reached via "sign in with a password instead" returns to the SSO step it
    // came from, preserving the provider; reached directly from email, back goes to Step 1.
    if (step.name === 'password' && step.fromSsoProvider) {
      setStep({ name: 'sso', email: step.email, provider: step.fromSsoProvider });
      return;
    }

    setStep({ name: 'email', email: step.name !== 'email' ? step.email : undefined });
  };

  const handleSwitchToPassword = () => {
    if (step.name === 'sso') {
      setStep({ name: 'password', email: step.email, fromSsoProvider: step.provider });
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <AuthLayout>
      {step.name === 'email' && <EmailStep initialEmail={step.email} onResolved={handleResolved} />}
      {step.name === 'sso' && (
        <SsoStep
          email={step.email}
          provider={step.provider}
          error={step.error}
          onBack={handleBack}
          onSwitchToPassword={handleSwitchToPassword}
        />
      )}
      {step.name === 'password' && <PasswordStep email={step.email} onBack={handleBack} />}
    </AuthLayout>
  );
};

export default Login;
